import hyper from "@macrostrat/hyper";
import { useContext } from "react";
import { useQuery } from "naukluft-data-backend";
import { ColumnContext, ColumnProvider } from "@macrostrat/column-components";
import { SummaryColumnProvider } from "./data-provider";
import { useSurfaces } from "~/sections/providers";
import styles from "./main.module.styl";
import {
  IncreaseDirection,
  Timescale,
  TimescaleOrientation,
  Interval,
} from "@macrostrat/timescale";
import chroma from "chroma-js";
import { ColumnDivision, useColumnDivisions } from "~/sections/column";

const h = hyper.styled(styles);

function buildTimescaleEntries(surfaces, names, keySection, range): Interval[] {
  let [minHeight, maxHeight] = range;

  let s1 = surfaces
    .filter((d) => d.type === "lithostrat") // only lithostratigraphy
    .map((d) => {
      // Get the surface height in the proper section
      const { section_height, ...rest } = d;
      return {
        height: section_height?.find((v) => v.section === keySection)?.height,
        ...rest,
      };
    })
    .filter((d) => d.height != null);

  s1.sort((a, b) => a.height - b.height);

  let formations = [];
  let members = [];

  let id = 1;
  let parentID = null;
  for (const d of s1) {
    const surfaceData = names.find((v) => v.id === d.upper_unit);
    if (surfaceData == null) {
      continue;
    }
    const { height } = d;
    const color = surfaceData.color;
    // Commonality is how closely related the units are to each other.
    if (surfaceData.level === 3) {
      // If we have a formation but not a member, push that to formations only
      formations.push({
        height,
        name: surfaceData.short_name,
        id,
        color,
      });
      id++;
      continue;
    }
    if (d.commonality === 2) {
      formations.push({
        height,
        name: surfaceData.formation_short_name,
        id,
        color,
      });
      parentID = id;
      id++;
    }
    members.push({
      height,
      name: surfaceData.short_name,
      parent: parentID,
      id,
      color,
    });
    id++;
  }

  minHeight = Math.max(minHeight, formations[0]?.height ?? minHeight);

  // Adjust min and max height as necessary

  // Iterate formations to add top heights
  const intervals = [
    {
      oid: 0,
      name: "Zebra River Group",
      eag: minHeight,
      lag: maxHeight,
      lvl: 0,
      col: "#CCCCCC",
      pid: null,
    },
  ];
  for (let i = 0; i < formations.length; i++) {
    const current = formations[i];
    const next = formations[i + 1];
    intervals.push({
      ...current,
      oid: current.id,
      nam: current.name,
      eag: current.height,
      lag: next?.height ?? maxHeight,
      pid: 0,
      lvl: 1,
      col: current.color,
    });
  }

  for (let i = 0; i < members.length; i++) {
    const current = members[i];
    const next = members[i + 1];
    intervals.push({
      ...current,
      oid: current.id,
      nam: current.name,
      eag: current.height,
      lag: next?.height ?? range[1],
      pid: current.parent,
      lvl: 2,
      col: current.color,
    });
  }

  return intervals;
}

export function LithostratigraphyColumn({ keySection = "J" }) {
  const names = useQuery("sections/summary/lithostratigraphy-names");
  let surfaces = useSurfaces();
  const { scale, range } = useContext(ColumnContext);

  if (names == null) {
    return null;
  }

  const intervals = buildTimescaleEntries(surfaces, names, keySection, range);

  return h(
    "div.lithostrat-key",
    h(Timescale, {
      orientation: TimescaleOrientation.VERTICAL,
      increaseDirection: IncreaseDirection.UP_RIGHT,
      absoluteAgeScale: true,
      showAgeAxis: false,
      intervals,
      rootInterval: 0,
      levels: [1, 2],
      intervalStyle,
      scale,
    }),
  );
}

const rangeForDivisions = (divisions: ColumnDivision[]): [number, number] => {
  if (divisions.length === 0) return [0, 0];

  const start = divisions[0].bottom;
  const end = divisions[divisions.length - 1].top;
  return [start, end];
};

export function GeneralizedLithostratKey(props) {
  let {
    padding = {
      left: 5,
      top: 30,
      right: 5,
      bottom: 10,
    },
    keySection = "Onis",
    pixelsPerMeter,
  } = props;
  let { left, right } = padding;

  let divisions = useColumnDivisions(keySection);
  const range = rangeForDivisions(divisions);

  return h(ColumnProvider, { range, divisions, zoom: 0.1, pixelsPerMeter }, [
    h(LithostratigraphyColumn, { keySection }),
  ]);
}

export function SummaryLithostratigraphyKey({
  keySection = "J",
  pixelsPerMeter,
}) {
  return h(SummaryColumnProvider, { id: keySection, pixelsPerMeter }, [
    h(LithostratigraphyColumn, { keySection }),
  ]);
}

function intervalStyle(interval) {
  return {
    backgroundColor: chroma(interval.color)
      .set("hsl.l", 0.9)
      .set("hsl.s", 0.3)
      .hex(),
    color: chroma(interval.color).set("hsl.l", 0.2).css(),
  };
}
